// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { api } from '@/lib/api';
import { resolveScopeDisplay, type ScopeDisplay } from '@/components/knowledge/util';
import type { KnowledgeScope } from '@greenlight/contract';

export interface ScopeRefs {
  scope: KnowledgeScope;
  integrationId: string | null;
  appId: string | null;
}

/**
 * Fetches the org's integrations + apps once (shared React-Query keys, so it
 * dedupes across cards) and returns a resolver that maps an entry/proposal's
 * scope refs to its display label + tile via O(1) id lookups.
 *
 * Note: only the first page of each list is fetched, so an entry or proposal
 * referencing an integration/app beyond that page falls back to its raw id.
 * Label resolution degrades gracefully, so this stays a single read rather than
 * walking every page the way `/integrations` does for its own list.
 */
export function useScopeResolvers(): (refs: ScopeRefs) => ScopeDisplay {
  const integ = api.integrations.list.useQuery(['integrations'], undefined, {
    queryKey: ['integrations'],
  });
  const appsQ = api.apps.list.useQuery(['apps'], { query: {} }, { queryKey: ['apps'] });

  const integrationsById = React.useMemo(() => {
    const m = new Map<string, { slug: string; name: string }>();
    if (integ.data?.status === 200) {
      for (const i of integ.data.body.items) m.set(i.id, { slug: i.slug, name: i.name });
    }
    return m;
  }, [integ.data]);

  const appsById = React.useMemo(() => {
    const m = new Map<string, { slug: string; name: string }>();
    if (appsQ.data?.status === 200) {
      for (const a of appsQ.data.body.items) m.set(a.app_id, { slug: a.slug, name: a.name });
    }
    return m;
  }, [appsQ.data]);

  return React.useCallback(
    (refs: ScopeRefs) => resolveScopeDisplay(refs, integrationsById, appsById),
    [integrationsById, appsById],
  );
}
